import React, { useState, useEffect } from "react";
import { X, ExternalLink, Info, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const CreditstarPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const creditstarUrl = "https://on.creditstar.fi/t/t?a=1971698767&as=1854031059&t=2&tk=1";
  
  const STORAGE_KEY = 'creditstar_popup_dismissed';
  const STORAGE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  useEffect(() => {
    // Check if popup was dismissed recently
    const dismissedData = localStorage.getItem(STORAGE_KEY);
    
    if (dismissedData) {
      try {
        const { timestamp } = JSON.parse(dismissedData);
        const now = Date.now();
        
        // If less than 24 hours have passed since dismissal, don't show popup
        if (now - timestamp < STORAGE_EXPIRY) {
          return;
        } else {
          // Remove expired data
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (error) {
        // If data is corrupted, remove it
        localStorage.removeItem(STORAGE_KEY);
      }
    }

    // Show popup after 15 seconds if not dismissed recently
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 15000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    
    // Save dismissal timestamp to localStorage
    const dismissalData = {
      timestamp: Date.now()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissalData));
  };

  const handleCreditstarClick = () => {
    window.open(creditstarUrl, '_blank', 'noopener,noreferrer');
    handleClose(); // Use handleClose to also save dismissal
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full relative shadow-2xl">
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 z-10"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="p-6">
          <div className="text-center mb-4">
            <img 
              src="/lovable-uploads/9aa19572-3de1-48ab-8516-c45c17ab8760.png" 
              alt="Creditstar" 
              className="h-10 mx-auto mb-3"
            />
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Tarvitsetko lainaa heti käyttöön?
            </h3>
            <div className="bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-sm font-bold inline-flex items-center mb-3">
              <Clock className="w-4 h-4 mr-1" />
              RAHAT HETI KÄYTÖSSÄ
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-4">
            <h4 className="font-semibold text-blue-900 mb-2">Creditstar - Lainaa 3000 € asti</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Rahat käytössä heti</li>
              <li>• Ei avausmaksua (0€)</li>
              <li>• Jatkuva luottolimiitti</li>
              <li>• Nopea hyväksyntä</li>
            </ul>
          </div>

          <div className="space-y-3">
            <Button 
              onClick={handleCreditstarClick}
              className="w-full bg-blue-700 hover:bg-blue-800 text-white font-semibold"
            >
              <Clock className="mr-2 h-4 w-4" />
              Hae luottoa heti
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
            
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full border-blue-700 text-blue-700 hover:bg-blue-50">
                  <Info className="mr-2 h-4 w-4" />
                  Lainaesimerkki ja ehdot
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-white">
                <DialogHeader>
                  <DialogTitle className="text-gray-900">Creditstar - Käyttöehdot ja lainaesimerkki</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 text-gray-700">
                  <div>
                    <h4 className="font-semibold mb-2 text-gray-900">Lainaesimerkki</h4>
                    <p className="text-sm text-gray-600">
                      Creditstar on vakuudeton 3000 euron asti jatkuva luotto, nimelliskorko 14,99%, 
                      luoton avausmaksu 0 euroa, tilinhoitomaksu 0,01% päivässä luottorajasta.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-2 text-gray-900">Esimerkki luoton takaisinmaksusta</h4>
                    <p className="text-sm text-gray-600">
                      Todellinen vuosikorko 2000 euron luotolle on 23,86%. Laskelmassa on huomioitu 
                      nimelliskorko 14,99%, lainanhoitokulu 6 euroa kuukaudessa. Lainan ja 
                      lainakustannusten yhteismäärä 2238,12 euroa, kun maksueriä on 12.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2 text-gray-900">Kohderyhmä</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Yli 21-vuotiaat</li>
                      <li>• Vakituisessa työsuhteessa</li>
                      <li>• Suomalainen pankkitili</li>
                      <li>• Asuu vakituisesti Suomessa</li>
                    </ul>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <button
              onClick={handleClose}
              className="w-full text-sm text-gray-500 hover:text-gray-700 py-2"
            >
              Ei kiitos, sulje
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditstarPopup;